import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/common/components/ui/dialog";
import { Button } from "@/common/components/ui/button";
import { Textarea } from "@/common/components/ui/textarea";
import { Label } from "@/common/components/ui/label";
import { Input } from "@/common/components/ui/input";
import { Upload, X, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { uploadToS3 } from "@/common/utils/s3Helpers";

const CLIENT_FUNCTION_BASE_URL = import.meta.env.VITE_CLIENT_FUNCTION_BASE_URL;

const IssueReportModal = ({
  isOpen,
  onClose,
  issueType = "course",
  contextId = null,
  additionalData = {},
}) => {
  const { user } = useAuth();
  const { profile: currentUser, token } = user;

  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload only image files");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setScreenshot(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setScreenshotPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim()) {
      toast.error("Please provide a description of the issue");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the data based on issue type
      const issueData = {
        description: description.trim(),
        issue_type: issueType,
        user_id: currentUser?.id,
        ...additionalData,
      };

      // Add context-specific IDs
      if (contextId) {
        switch (issueType) {
          case "course":
            issueData.course_id = contextId;
            break;
          case "assessment":
            issueData.assessment_id = contextId;
            break;
          case "question":
            issueData.question_id = contextId;
            break;
          default:
            // For other types, we might need to handle differently
            break;
        }
      }

      // Add university_id if available
      if (currentUser.university_id) {
        issueData.university_id = currentUser.university_id;
      }

      // Upload screenshot to S3 if provided
      if (screenshot) {
        try {
          const timestamp = Date.now();
          const filename = `issue_screenshot_${timestamp}.${
            screenshot.type.split("/")[1]
          }`;

          console.log("Uploading screenshot to S3...");
          const uploadResult = await uploadToS3({
            file: screenshot,
            path: `platform_issues/screenshots/${filename}`,
            token: token,
            type: screenshot.type,
          });

          console.log("Screenshot upload result:", uploadResult);
          issueData.screenshot_url = uploadResult.path;
        } catch (uploadError) {
          console.error("Error uploading screenshot:", uploadError);
          toast.error(
            "Failed to upload screenshot. Issue will be submitted without screenshot."
          );
        }
      }

      const response = await fetch(
        `${CLIENT_FUNCTION_BASE_URL}/platform_issues`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(issueData),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(
          "Issue reported successfully! Our team will review it shortly."
        );
        handleClose();
      } else {
        throw new Error(result.message || "Failed to report issue");
      }
    } catch (error) {
      console.error("Error reporting issue:", error);
      toast.error(error.message || "Failed to report issue. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDescription("");
    setScreenshot(null);
    setScreenshotPreview(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-800">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            Report an Issue
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-gray-600">
            Help us improve by reporting any issues you encounter. Please
            provide as much detail as possible.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="description"
              className="text-sm font-medium text-gray-700"
            >
              Issue Description *
            </Label>
            <Textarea
              id="description"
              placeholder="Please describe the issue you're experiencing in detail..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px] resize-none"
              required
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Screenshot (Optional)
            </Label>
            <div className="space-y-3">
              {!screenshotPreview ? (
                <div className="relative">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshotChange}
                    className="hidden"
                    id="screenshot-upload"
                  />
                  <Label
                    htmlFor="screenshot-upload"
                    className="flex items-center justify-center w-full px-4 py-6 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col items-center space-y-2 text-gray-600">
                      <Upload className="w-6 h-6" />
                      <div className="text-sm text-center">
                        <span className="font-medium">Click to upload</span> or
                        drag and drop
                        <br />
                        <span className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 5MB
                        </span>
                      </div>
                    </div>
                  </Label>
                </div>
              ) : (
                <div className="relative">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="w-full max-h-48 object-contain rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={removeScreenshot}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </form>

        <DialogFooter className="px-6 py-4 border-t">
          <div className="flex gap-3 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              disabled={isSubmitting || !description.trim()}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                  {screenshot ? "Uploading & Reporting..." : "Reporting..."}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Report Issue
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IssueReportModal;
